import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import React, { useContext, useEffect, useState } from 'react';
import {
  EntityTypeEnum,
  MessageTypeFilterEnum,
  NotificationPreviewListOutDTO,
  PageResponseNotificationPreviewListOutDTO,
  SupplierNotificationTypeEnum,
} from '@api/ringil-api.v2';
import {
  dayjsFromApiPrague,
  dayjsFromApiUtcInTz,
  formatDateFromDayjsWithLocale,
  stringDateTimeWithLocaleFromApiStringInTz,
} from '@utils/dateWithTz';
import { Button } from 'primereact/button';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { useTranslation } from 'react-i18next';
import { FilterPage, useFilter } from '@hooks/useFilter/Filter.tsx';
import ExpeditionLocation from '@components/obsolete/ExpeditionLocation/ExpeditionLocation.tsx';
import ButtonLayout from '@components/obsolete/Form/ButtonLayout/ButtonLayout.tsx';
import { proxiedPropertiesOf } from '@hooks/useForm/useForm.tsx';
import NavTitle from '@components/obsolete/NavTitle/NavTitle.tsx';
import InputNotificationType from '@components/obsolete/Form/InputNotificationType/InputNotificationType.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';

interface Filter {
  messageTypeFilter: MessageTypeFilterEnum;
  notificationType?: string;
  onlyMessage: boolean;
}

const AllNotificationsList = () => {
  const { t } = useTranslation('common');
  const { loggedUser} = useContext(AuthenticatedUserContext);
  const { cuListNotifications, cuReadNotification, cuMarkAllNotificationAsRead } = useContext(ApiContext);
  const [pageData, setPageData] = useState<PageResponseNotificationPreviewListOutDTO>();

  const nav = useNavigation();

  const { page, onPage, applyPaging, filter, Filter, applyFilterValue } = useFilter<Filter>({
    onLoad: (page: FilterPage, filter: Filter) => {
      loadData(page.pageSize, page.pageNumber, filter);
    },
    filterName: 'all_notifications_list',
  });

  useEffect(() => {
    loadData(page.pageSize, page.pageNumber, filter);
  }, []);

  const maskAsReadAndRedirect = (rowData: NotificationPreviewListOutDTO) => {
    cuReadNotification(rowData.id, rowData.companyId, rowData.applicationId, () => {
      if (rowData.entityType == EntityTypeEnum.SUPPLIER_NOTIFICATION) {
        nav.navigate(nav.urlFunctions.createSupplierNotificationDetail(rowData.entityId, SupplierNotificationTypeEnum.EFXX));
      } else if (rowData.entityType == EntityTypeEnum.GROUP_DEMAND) {
        nav.navigate(nav.urlFunctions.createMultiDemanDetail(rowData.entityId));
      } else {
        const openMessages = rowData.isMessage === true;
        nav.navigate(nav.urlFunctions.createExpeditionDetailV2(rowData.entityId, openMessages, rowData.selectedCarrierId ?? undefined));
      }
    });
  };

  const loadData = (pageSize: number, pageNumber: number, filter: Filter) => {
    cuListNotifications(
      {
        messageTypeFilter: filter?.messageTypeFilter,
        notificationType: filter?.notificationType,
        onlyMessage: filter?.onlyMessage,
      },
      { pageSize, pageNumber: pageNumber },
      data => {
        setPageData(data);
      },
    );
  };

  const markAllAsRead = () => {
    cuMarkAllNotificationAsRead(() => {
      loadData(page.pageSize, page.pageNumber, filter);
    });
  };

  const notificationStatusBodyTemplate = (rowData: NotificationPreviewListOutDTO) => {
    return (
      <div className="notification-status">
        <div className="notification-status-item notification-status-item__type text-blue">
          <strong>{rowData.name}</strong>
        </div>
        <div className="notification-status-item notification-status-item__name">
          <strong>{rowData.createdBy}</strong>
        </div>
        <div className="notification-status-item notification-status-item__date-time text-gray5">
          <span>{stringDateTimeWithLocaleFromApiStringInTz(rowData.createAt, loggedUser.defaultTimezoneId)}</span>
        </div>
        <COTimezone timezoneId={loggedUser.defaultTimezoneId} />
      </div>
    );
  };

  const expeditionBodyTemplate = (rowData: NotificationPreviewListOutDTO) => {
    return (
      <div className="notification-expedition">
        <div className="notification-expedition-item">
          <span className="text-gray5">{t(`AllNotificationsList.expedition`, `Expedition`)}</span> <span
          className="text-blue">{rowData.applicationId}</span>
        </div>
        <div className="notification-expedition-item">
          <span className="text-gray5">
            {t(`AllNotificationsList.loadingFromEscaped`, {
              date: formatDateFromDayjsWithLocale(
                rowData.timezoneId
                  ? dayjsFromApiUtcInTz(rowData.toLoadAt, rowData.timezoneId)
                  : dayjsFromApiPrague(rowData.toLoadAt),
              ),
            })}
          </span>
          <COTimezone timezoneId={rowData.timezoneId} />
        </div>
      </div>
    );
  };

  const routeBodyTemplate = (rowData: NotificationPreviewListOutDTO) => {
    return (
      <div className="locations">
        <ExpeditionLocation locationPoint={t(`AllNotificationsList.from`, `From`)}
                            locationAddress={rowData.locationNames[0]} wrapper="LARGE" />
        <ExpeditionLocation
          locationPoint={t(`AllNotificationsList.to`, `To`)}
          locationAddress={rowData.locationNames[rowData.locationNames.length - 1]}
          wrapper="LARGE"
        />
        {rowData.locationNames.length > 2 && (
          <ExpeditionLocation
            locationPoint={t(`AllNotificationsList.through`, `through`)}
            through={t(`AllNotificationList.through`, {
              count: rowData.locationNames.length - 2,
            })}
            wrapper="LARGE"
          />
        )}
      </div>
    );
  };

  const navBodyTemplate = (rowData: NotificationPreviewListOutDTO) => {
    return (
      <div>
        <ButtonLayout>
          <Button
            className="btn-detail p-button p-button-rounded p-button-outlined secondary-btn"
            onClick={() => maskAsReadAndRedirect(rowData)}
          >
            <i className="icon pi pi-ellipsis-h"></i> <span className="btn-name">{t('allShipments.detail')}</span>
          </Button>
        </ButtonLayout>
        <div className="notification-status">
          <strong>{rowData.read ? t('AllNotificationsList.read', 'přečteno') : t('AllNotificationsList.unread', 'nepřečteno')}</strong>
        </div>
      </div>
    );
  };
  const { tr } = useTranslationLgs();
  const names = proxiedPropertiesOf<Filter>();

  const modelFilterType = [
    {
      label: t('AllNotificationsList.UNREAD', 'Unread'),
      code: MessageTypeFilterEnum.UNREAD,
    },
    {
      label: t('AllNotificationsList.READ', 'Read'),
      code: MessageTypeFilterEnum.READ,
    },
    {
      label: t('AllNotificationsList.BOTH', 'Both'),
      code: MessageTypeFilterEnum.BOTH,
    },
  ];

  const modelOnlyMessages = [
    {
      code: 'ALL',
      label: tr('Notification.all', 'all'),
    },
    { code: 'USER_MESSAGES', label: tr('Notification.userMessages', 'User messages') },
  ];

  return (
    <>
      <NavTitle wrapper="PADDING-0">
        <h2 className="h2-title p-m-0">{t(`AllNotificationsList.notifications`, `Notifications`)}</h2>
        <Button label={tr(`AllNotificationsList.markAllAsRead`, `Mark all as read`)} onClick={() => markAllAsRead()} />
      </NavTitle>
      <Filter />
      {filter && (
        <div className="p-mt-3 p-mb-2">
          <div className="p-formgroup-inline">
            <div className="p-field">
              <InputNotificationType value={filter?.notificationType}
                                     onChange={v => applyFilterValue(names.notificationType, v)} />
            </div>
            <div className="p-field">
              <InputSelect
                model={modelFilterType}
                selectedItemCode={filter?.messageTypeFilter}
                onSelect={v => applyFilterValue(names.messageTypeFilter, v as MessageTypeFilterEnum)}
                />
            </div>
            <div className="p-field">
              <InputSelect
                model={modelOnlyMessages}
                selectedItemCode={filter?.onlyMessage ? 'USER_MESSAGES' : 'ALL'}
                onSelect={v => applyFilterValue(names.onlyMessage, v === 'USER_MESSAGES')}
              />
            </div>
          </div>
        </div>
      )}
      {pageData && pageData.data && (
        <>
          {/*// @ts-ignore*/}
          <DataTable
            emptyMessage={t(`global.noRecordFound`, `Nebyly nalezeny žádné záznamy`)}
            className="default-table notification-table"
            value={pageData.data}
            rows={pageData.size}
            totalRecords={pageData.total}
            onPage={onPage}
            first={(pageData.page - 1) * pageData.size}
            paginatorPosition={'both'}
            lazy={true}
            paginator={true}
          >
            <Column className="notification-table__notification-status" body={notificationStatusBodyTemplate}></Column>
            <Column className="notification-table__expedition" body={expeditionBodyTemplate}></Column>
            <Column className="notification-table__route" body={routeBodyTemplate}></Column>
            <Column className="notification-table__nav" body={navBodyTemplate}></Column>
          </DataTable>
        </>
      )}
    </>
  );
};
export default AllNotificationsList;
